/**
 * Modale Operatori — rubrica per movimenti/FIR .
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { endpoints, type ContactItem } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChoiceSelect, FormField } from "@/components/forms";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { RUOLO_CONTATTO } from "@/lib/rentriChoices";

const ROLES = ["produttore", "trasportatore", "destinatario", "intermediario"] as const;

function validate(form: Partial<ContactItem>): string | null {
  if (!form.denominazione?.trim()) return "Indica la denominazione.";
  if (!form.codice_fiscale?.trim() && !form.partita_iva?.trim()) {
    return "Inserisci codice fiscale o partita IVA.";
  }
  return null;
}

export function OperatoreDialog({
  open,
  onOpenChange,
  contact,
  defaultRole,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact?: ContactItem | null;
  defaultRole?: string;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = Boolean(contact?.id);
  const [form, setForm] = useState<Partial<ContactItem>>({
    role: "produttore",
    denominazione: "",
    nazione: "IT",
  });
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [newSito, setNewSito] = useState("");
  const [newSitoLabel, setNewSitoLabel] = useState("");

  const sitesQ = useQuery({
    queryKey: ["contact-sites", contact?.id],
    queryFn: () => endpoints.contacts.sites.list(contact!.id),
    enabled: open && isEdit && !!contact?.id,
  });

  useEffect(() => {
    if (!open) return;
    setFieldError(null);
    if (contact) {
      setForm({ ...contact });
    } else {
      setForm({
        role: (defaultRole && ROLES.includes(defaultRole as (typeof ROLES)[number])
          ? defaultRole
          : "produttore") as ContactItem["role"],
        denominazione: "",
        codice_fiscale: "",
        partita_iva: "",
        rentri_iscrizione: "",
        comune: "",
        provincia: "",
        cap: "",
        indirizzo: "",
        nazione: "IT",
      });
    }
  }, [open, contact, defaultRole]);

  const save = useMutation({
    mutationFn: async () => {
      const err = validate(form);
      if (err) throw new Error(err);
      if (isEdit && contact) {
        return endpoints.contacts.update(contact.id, form);
      }
      return endpoints.contacts.create(form);
    },
    onSuccess: () => {
      toast.success(isEdit ? "Operatore aggiornato" : "Operatore aggiunto");
      onSaved();
      onOpenChange(false);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Errore salvataggio";
      setFieldError(msg);
      toast.error(msg);
    },
  });

  const addSite = useMutation({
    mutationFn: () => {
      if (!contact?.id) throw new Error("Salva prima l'operatore");
      const num = newSito.trim();
      if (!num) throw new Error("Inserisci num_iscr_sito");
      return endpoints.contacts.sites.add(contact.id, {
        num_iscr_sito: num,
        label: newSitoLabel.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success("Unità locale aggiunta");
      setNewSito("");
      setNewSitoLabel("");
      qc.invalidateQueries({ queryKey: ["contact-sites", contact?.id] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Errore"),
  });

  const removeSite = useMutation({
    mutationFn: (siteId: string) => {
      if (!contact?.id) throw new Error("Contatto non valido");
      return endpoints.contacts.sites.remove(contact.id, siteId);
    },
    onSuccess: () => {
      toast.success("Unità locale rimossa");
      qc.invalidateQueries({ queryKey: ["contact-sites", contact?.id] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Errore"),
  });

  const set = <K extends keyof ContactItem>(k: K, v: ContactItem[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica operatore" : "Nuovo operatore"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="Ruolo" required>
            <ChoiceSelect
              value={form.role ?? "produttore"}
              onChange={(v) => set("role", v || "produttore")}
              options={RUOLO_CONTATTO}
              required
              disabled={isEdit}
            />
          </FormField>
          <FormField label="Denominazione" required>
            <Input
              value={form.denominazione ?? ""}
              onChange={(e) => set("denominazione", e.target.value)}
            />
          </FormField>
          <FormField label="Partita IVA">
            <Input
              value={form.partita_iva ?? ""}
              onChange={(e) => set("partita_iva", e.target.value)}
              className="font-mono"
            />
          </FormField>
          <FormField label="Codice fiscale">
            <Input
              value={form.codice_fiscale ?? ""}
              onChange={(e) => set("codice_fiscale", e.target.value)}
              className="font-mono"
            />
          </FormField>
          <FormField label="Iscrizione RENTRI">
            <Input
              value={form.rentri_iscrizione ?? ""}
              onChange={(e) => set("rentri_iscrizione", e.target.value)}
              className="font-mono"
            />
          </FormField>
          <FormField label="Comune">
            <Input value={form.comune ?? ""} onChange={(e) => set("comune", e.target.value)} />
          </FormField>
          <FormField label="Provincia">
            <Input
              value={form.provincia ?? ""}
              onChange={(e) => set("provincia", e.target.value)}
            />
          </FormField>
          <FormField label="CAP">
            <Input value={form.cap ?? ""} onChange={(e) => set("cap", e.target.value)} />
          </FormField>
          <div className="sm:col-span-2">
            <FormField label="Indirizzo">
              <Input
                value={form.indirizzo ?? ""}
                onChange={(e) => set("indirizzo", e.target.value)}
              />
            </FormField>
          </div>
        </div>

        {isEdit && contact && (
          <div className="mt-4 space-y-3 border-t pt-4">
            <p className="text-sm font-medium">Unità locali (destinatari FIR)</p>
            <p className="text-xs text-muted-foreground">
              Associa i siti RENTRI usati come destinatario nei formulari.
            </p>
            {(sitesQ.data?.length ?? 0) > 0 && (
              <Table>
                <THead>
                  <TR>
                    <TH>num_iscr_sito</TH>
                    <TH>Etichetta</TH>
                    <TH className="w-12" />
                  </TR>
                </THead>
                <TBody>
                  {sitesQ.data!.map((s) => (
                    <TR key={s.id}>
                      <TD className="font-mono text-xs">{s.num_iscr_sito}</TD>
                      <TD className="text-sm">{s.label ?? "—"}</TD>
                      <TD>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          aria-label="Rimuovi unità locale"
                          onClick={() => removeSite.mutate(s.id)}
                          disabled={removeSite.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <FormField label="num_iscr_sito">
                <Input
                  className="font-mono text-sm"
                  value={newSito}
                  onChange={(e) => setNewSito(e.target.value)}
                  placeholder="OP…-TO0001"
                />
              </FormField>
              <FormField label="Etichetta (opz.)">
                <Input
                  value={newSitoLabel}
                  onChange={(e) => setNewSitoLabel(e.target.value)}
                  placeholder="Sede principale"
                />
              </FormField>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addSite.mutate()}
              disabled={!newSito.trim() || addSite.isPending}
            >
              <Plus className="mr-1.5 h-4 w-4" /> Aggiungi unità locale
            </Button>
          </div>
        )}

        {fieldError && (
          <p className="text-sm text-destructive mt-2">{fieldError}</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Salvataggio…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
